/**
 * Canonical class-size and pace-grouping copy for public marketing surfaces.
 *
 * What: One source of truth for the "maximum 4 students per class" cap and the
 * pace-grouping policy that follows from it — as a fact-grid entry, an FAQ
 * item (which also feeds the FAQPage JSON-LD on every page that renders it),
 * and the short lines used inside pricing copy.
 * Why: Class size is the first question parents ask and the strongest quality
 * signal when they compare DeepThink against cheaper group tuition. Restating
 * the cap as free text on each surface drifts — one number phrased five
 * different ways reads as marketing rather than a policy. Copy voice: state
 * the fact and the concrete benefit to the child; never coach comparisons
 * against other programmes, cite our own costs, or dwell on the bad outcome
 * we avoid.
 * When: Update when the cap or the pace-grouping policy changes. `public/llms.txt`
 * is a static file and is NOT generated from this module — edit it alongside.
 */

export const MAX_CLASS_SIZE = 4;

/** Short value string for fact grids and badges. */
export const CLASS_SIZE_VALUE = `Maximum ${MAX_CLASS_SIZE} students`;

/** One-sentence policy line for bullet lists and supporting copy. */
export const PACE_GROUPING_LINE = `Students are grouped by learning pace, not just by level — if no existing session matches your child’s pace, we open a new one.`;

/** Fact-grid entry. Shape matches the landing, level, and school fact grids. */
export const CLASS_SIZE_FACT = {
  label: 'Class size',
  value: CLASS_SIZE_VALUE,
  detail: PACE_GROUPING_LINE,
};

/**
 * FAQ entry. Rendered as visible copy and emitted into FAQPage JSON-LD, so it
 * is written to be quotable on its own — the answer opens with the number.
 */
export const CLASS_SIZE_FAQ = {
  question: 'How many students are in each class?',
  answer: `Four, at most. Students are grouped by learning pace, not just by year level, so each lesson moves at the speed of the students actually in it. When no session matches a child’s pace, we open a new one. In a class that size, every child answers questions, gets their working checked, and gets help the moment they need it.`,
};

/** Line for pricing sections, where parents are actively comparing fees. */
export const CLASS_SIZE_PRICING_NOTE = `A class of four means the teacher sees every child’s working — and teaches to it, every week.`;

/** Compact bullet for feature lists that only have room for one line. */
export const CLASS_SIZE_BULLET = `Maximum ${MAX_CLASS_SIZE} students per class, grouped by learning pace`;
